/**
 * Supabase Auth Client and Session Management
 *
 * Provides a centralized wrapper around Supabase Auth (GoTrue) for user authentication,
 * registration, session persistence, and logout. Lazy-initializes the Supabase client
 * to ensure environment configuration is available before client creation.
 *
 * Features:
 * - Email/password login and registration
 * - Magic link sign-in (OTP-based passwordless auth)
 * - Session persistence with automatic recovery
 * - Password reset and email change flows
 * - Secure logout with session revocation
 * - Error handling with user-friendly messages
 *
 * Implementation details:
 * - Client is lazily initialized on first use to prevent white-screen crashes if env is missing
 * - Uses PKCE (Proof Key for Code Exchange) for OAuth flows
 * - Relies on Supabase built-in session management (no manual JWT storage)
 * - Integrates with Supabase Auth's email confirmation and recovery flows
 *
 * See:
 * - docs/v2/03-auth-and-security.md
 * - https://supabase.com/docs/guides/auth
 */

import { createClient, type SupabaseClient, type Session, type User } from '@supabase/supabase-js';
import type { Database } from '@herohire/types';

/**
 * Lazy-initialized Supabase client
 * Set on first use to ensure environment config is available
 */
let supabaseClient: SupabaseClient<Database> | null = null;

/**
 * Minimal shape of runtime config this module needs to construct a
 * Supabase client. Deliberately structural (rather than importing the full
 * `RuntimeConfig` type from `@/lib/config/runtimeConfig`) to avoid a
 * circular dependency between the auth and config modules.
 */
export interface SupabaseRuntimeConfig {
  supabaseUrl: string;
  supabaseAnonKey: string;
}

/**
 * Configure (or reconfigure) the Supabase Auth client from runtime
 * configuration.
 *
 * This is the preferred entry point for initializing the client: it
 * replaces the previous pattern of reading
 * `import.meta.env.VITE_SUPABASE_URL` / `VITE_SUPABASE_ANON_KEY` directly,
 * so the same build can point at a different Supabase project per
 * environment (dev/stage/prod) without a rebuild — see
 * `docs/v2/05-runtime-configuration.md`.
 *
 * `AuthProvider` (src/app/providers.tsx) calls this once, on mount, with
 * the config it receives from `useConfig()`. Safe to call more than once;
 * later calls replace the cached client.
 *
 * @param config - `{ supabaseUrl, supabaseAnonKey }` sourced from RuntimeConfig
 * @returns Initialized Supabase client, or null if config is invalid
 */
export function configureSupabaseClient(
  config: SupabaseRuntimeConfig
): SupabaseClient<Database> | null {
  if (!config?.supabaseUrl || !config?.supabaseAnonKey) {
    console.warn(
      '[Supabase Auth] Runtime config is missing supabaseUrl or supabaseAnonKey; auth will not be available'
    );
    return null;
  }

  try {
    supabaseClient = createClient<Database>(config.supabaseUrl, config.supabaseAnonKey, {
      auth: {
        persistSession: true,
        detectSessionInUrl: true,
        autoRefreshToken: true,
      },
    });
    return supabaseClient;
  } catch (error) {
    console.error('[Supabase Auth] Failed to initialize client from runtime config:', error);
    return null;
  }
}

/**
 * Get the current Supabase Auth client, initializing it if necessary.
 *
 * Returns the client already configured via `configureSupabaseClient()`
 * (the normal path once `AuthProvider` has mounted — see
 * `src/app/providers.tsx` / `src/app/AppShell.tsx`, which always mount
 * `AuthProvider` below `ConfigProvider`, so `configureSupabaseClient()` has
 * run by the time any authenticated route can call an auth function here).
 *
 * Migrated (see "Migrate existing frontend environment variables to
 * runtime config format"): this used to fall back to build-time
 * `import.meta.env.VITE_SUPABASE_URL` / `VITE_SUPABASE_ANON_KEY` when
 * nothing had configured the client yet. That fallback has been removed —
 * the build must not depend on `VITE_SUPABASE_*` directly. If this is
 * called before `configureSupabaseClient()` has run (e.g. a bug, or a test
 * that forgot to call it), it now returns `null` and logs a warning rather
 * than silently reading a build-time env var.
 *
 * @returns Initialized Supabase client or null if `configureSupabaseClient()`
 *   has not been called yet with valid runtime config
 */
export function getSupabaseClient(): SupabaseClient<Database> | null {
  if (supabaseClient) {
    return supabaseClient;
  }

  console.warn(
    '[Supabase Auth] Supabase client not configured. Call configureSupabaseClient() with runtime config ' +
      '(see @/lib/config/runtimeConfig and @/app/providers AuthProvider); auth will not be available'
  );
  return null;
}

/**
 * Response shape for authentication operations
 */
export interface AuthResponse {
  user: User | null;
  session: Session | null;
  error: Error | null;
}

/**
 * Response shape for magic link operations
 */
export interface MagicLinkResponse {
  success: boolean;
  error: Error | null;
}

/**
 * Sign in with email and password
 *
 * Authenticates an existing user using their email and password.
 * On success, establishes a session and returns the user and session.
 *
 * @param email - User's email address
 * @param password - User's password
 * @returns AuthResponse with user/session or error
 *
 * @example
 * const { user, session, error } = await loginWithEmail('user@example.com', 'password123');
 * if (error) {
 *   console.error('Login failed:', error.message);
 * } else {
 *   console.log('Logged in as:', user.email);
 * }
 */
export async function loginWithEmail(
  email: string,
  password: string
): Promise<AuthResponse> {
  const client = getSupabaseClient();
  if (!client) {
    return {
      user: null,
      session: null,
      error: new Error('Supabase client not initialized'),
    };
  }

  try {
    const { data, error } = await client.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      return {
        user: null,
        session: null,
        error: new Error(error.message),
      };
    }

    return {
      user: data.user,
      session: data.session,
      error: null,
    };
  } catch (error) {
    return {
      user: null,
      session: null,
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }
}

/**
 * Sign up with email and password
 *
 * Creates a new user account with the provided email and password.
 * Depending on Supabase project settings, may require email confirmation
 * before the account is fully active.
 *
 * @param email - Email address for the new account
 * @param password - Password for the new account
 * @param metadata - Optional user metadata (e.g., full name, profile data)
 * @returns AuthResponse with user/session or error
 *
 * @example
 * const { user, session, error } = await signUpWithEmail(
 *   'newuser@example.com',
 *   'securePassword123',
 *   { full_name: 'John Doe' }
 * );
 * if (!error) {
 *   console.log('Account created. Check email for confirmation.');
 * }
 */
export async function signUpWithEmail(
  email: string,
  password: string,
  metadata?: Record<string, unknown>
): Promise<AuthResponse> {
  const client = getSupabaseClient();
  if (!client) {
    return {
      user: null,
      session: null,
      error: new Error('Supabase client not initialized'),
    };
  }

  try {
    const { data, error } = await client.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
      },
    });

    if (error) {
      return {
        user: null,
        session: null,
        error: new Error(error.message),
      };
    }

    return {
      user: data.user,
      session: data.session,
      error: null,
    };
  } catch (error) {
    return {
      user: null,
      session: null,
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }
}

/**
 * Send a magic link (OTP) for passwordless sign-in
 *
 * Generates a one-time-password (OTP) and sends it to the provided email.
 * User can use the OTP to sign in without a password, or click a magic link.
 *
 * @param email - Email address to send magic link to
 * @returns MagicLinkResponse with success status and optional error
 *
 * @example
 * const { success, error } = await sendMagicLink('user@example.com');
 * if (success) {
 *   console.log('Check your email for the sign-in link');
 * }
 */
export async function sendMagicLink(email: string): Promise<MagicLinkResponse> {
  const client = getSupabaseClient();
  if (!client) {
    return {
      success: false,
      error: new Error('Supabase client not initialized'),
    };
  }

  try {
    const { error } = await client.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: true,
      },
    });

    if (error) {
      return {
        success: false,
        error: new Error(error.message),
      };
    }

    return {
      success: true,
      error: null,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }
}

/**
 * Verify an OTP (one-time password) for magic link or 2FA
 *
 * Completes the OTP sign-in flow by verifying the code sent to the user's email.
 * Establishes a session if verification succeeds.
 *
 * @param email - Email address that received the OTP
 * @param token - OTP code from email
 * @param type - Type of OTP flow ('email' for magic link, 'sms' for SMS)
 * @returns AuthResponse with user/session or error
 *
 * @example
 * const { user, session, error } = await verifyOtp('user@example.com', '123456', 'email');
 * if (!error) {
 *   console.log('Signed in via magic link');
 * }
 */
export async function verifyOtp(
  email: string,
  token: string,
  type: 'email' | 'sms' = 'email'
): Promise<AuthResponse> {
  const client = getSupabaseClient();
  if (!client) {
    return {
      user: null,
      session: null,
      error: new Error('Supabase client not initialized'),
    };
  }

  try {
    const { data, error } = await client.auth.verifyOtp({
      email,
      token,
      type,
    });

    if (error) {
      return {
        user: null,
        session: null,
        error: new Error(error.message),
      };
    }

    return {
      user: data.user,
      session: data.session,
      error: null,
    };
  } catch (error) {
    return {
      user: null,
      session: null,
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }
}

/**
 * Request a password reset link
 *
 * Sends a password reset link to the user's email.
 * User must click the link and submit a new password to complete the reset.
 *
 * @param email - Email address for password reset
 * @returns Object with success status and optional error
 *
 * @example
 * const { success, error } = await requestPasswordReset('user@example.com');
 * if (success) {
 *   console.log('Reset link sent to email');
 * }
 */
export async function requestPasswordReset(
  email: string
): Promise<{ success: boolean; error: Error | null }> {
  const client = getSupabaseClient();
  if (!client) {
    return {
      success: false,
      error: new Error('Supabase client not initialized'),
    };
  }

  try {
    const { error } = await client.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`,
    });

    if (error) {
      return {
        success: false,
        error: new Error(error.message),
      };
    }

    return {
      success: true,
      error: null,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }
}

/**
 * Reset password with recovery token
 *
 * Called after user clicks password reset link and enters a new password.
 * Validates the recovery token and updates the password.
 *
 * @param newPassword - New password to set
 * @returns AuthResponse with updated user/session or error
 *
 * @example
 * const { user, session, error } = await resetPassword('newPassword123');
 * if (!error) {
 *   console.log('Password reset successfully');
 * }
 */
export async function resetPassword(
  newPassword: string
): Promise<AuthResponse> {
  const client = getSupabaseClient();
  if (!client) {
    return {
      user: null,
      session: null,
      error: new Error('Supabase client not initialized'),
    };
  }

  try {
    const { data, error } = await client.auth.updateUser({
      password: newPassword,
    });

    if (error) {
      return {
        user: null,
        session: null,
        error: new Error(error.message),
      };
    }

    return {
      user: data.user,
      session: null, // Session doesn't change on password update
      error: null,
    };
  } catch (error) {
    return {
      user: null,
      session: null,
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }
}

/**
 * Update user email with confirmation
 *
 * Initiates an email change request. If email confirmation is enabled,
 * a confirmation link will be sent to the new email address.
 *
 * @param newEmail - New email address
 * @returns Object with success status and optional error
 *
 * @example
 * const { success, error } = await updateEmail('newemail@example.com');
 * if (success) {
 *   console.log('Confirmation link sent to new email');
 * }
 */
export async function updateEmail(
  newEmail: string
): Promise<{ success: boolean; error: Error | null }> {
  const client = getSupabaseClient();
  if (!client) {
    return {
      success: false,
      error: new Error('Supabase client not initialized'),
    };
  }

  try {
    const { error } = await client.auth.updateUser({
      email: newEmail,
    });

    if (error) {
      return {
        success: false,
        error: new Error(error.message),
      };
    }

    return {
      success: true,
      error: null,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }
}

/**
 * Get the current session
 *
 * Retrieves the current authenticated session, if one exists.
 * Can be used to check if a user is logged in.
 *
 * @returns Current session or null if not authenticated
 *
 * @example
 * const session = await getCurrentSession();
 * if (session) {
 *   console.log('User is logged in');
 * }
 */
export async function getCurrentSession(): Promise<Session | null> {
  const client = getSupabaseClient();
  if (!client) {
    return null;
  }

  try {
    const {
      data: { session },
    } = await client.auth.getSession();
    return session;
  } catch (error) {
    console.error('[Supabase Auth] Failed to get current session:', error);
    return null;
  }
}

/**
 * Get the currently authenticated user
 *
 * Returns the user object for the current session, if one exists.
 *
 * @returns Current user or null if not authenticated
 *
 * @example
 * const user = await getCurrentUser();
 * if (user) {
 *   console.log('User email:', user.email);
 * }
 */
export async function getCurrentUser(): Promise<User | null> {
  const client = getSupabaseClient();
  if (!client) {
    return null;
  }

  try {
    const {
      data: { user },
    } = await client.auth.getUser();
    return user;
  } catch (error) {
    console.error('[Supabase Auth] Failed to get current user:', error);
    return null;
  }
}

/**
 * Listen for authentication state changes
 *
 * Subscribes to auth state changes (sign-in, sign-out, session refresh).
 * Returns an unsubscribe function to clean up the listener.
 *
 * @param callback - Function called when auth state changes
 * @returns Unsubscribe function to remove the listener
 *
 * @example
 * const unsubscribe = onAuthStateChange((event, session) => {
 *   console.log('Auth event:', event);
 *   if (session) {
 *     console.log('User is logged in');
 *   } else {
 *     console.log('User is logged out');
 *   }
 * });
 *
 * // Later, clean up:
 * unsubscribe();
 */
export function onAuthStateChange(
  callback: (event: string, session: Session | null) => void
): (() => void) | null {
  const client = getSupabaseClient();
  if (!client) {
    return null;
  }

  try {
    const {
      data: { subscription },
    } = client.auth.onAuthStateChange((event, session) => {
      callback(event, session);
    });

    return () => {
      subscription.unsubscribe();
    };
  } catch (error) {
    console.error('[Supabase Auth] Failed to set up auth state listener:', error);
    return null;
  }
}

/**
 * Sign out the current user
 *
 * Invalidates the current session and clears all stored session data.
 * After calling this, all subsequent authenticated requests will fail.
 *
 * @returns Object with success status and optional error
 *
 * @example
 * const { success, error } = await logout();
 * if (success) {
 *   console.log('Logged out successfully');
 * }
 */
export async function logout(): Promise<{
  success: boolean;
  error: Error | null;
}> {
  const client = getSupabaseClient();
  if (!client) {
    return {
      success: false,
      error: new Error('Supabase client not initialized'),
    };
  }

  try {
    const { error } = await client.auth.signOut();

    if (error) {
      return {
        success: false,
        error: new Error(error.message),
      };
    }

    return {
      success: true,
      error: null,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }
}

/**
 * Sign out from all devices
 *
 * Invalidates all sessions for the current user across all devices.
 * All existing tokens become invalid.
 *
 * @returns Object with success status and optional error
 *
 * @example
 * const { success, error } = await logoutAllDevices();
 * if (success) {
 *   console.log('All sessions invalidated');
 * }
 */
export async function logoutAllDevices(): Promise<{
  success: boolean;
  error: Error | null;
}> {
  const client = getSupabaseClient();
  if (!client) {
    return {
      success: false,
      error: new Error('Supabase client not initialized'),
    };
  }

  try {
    const { error } = await client.auth.signOut({ scope: 'others' });

    if (error) {
      return {
        success: false,
        error: new Error(error.message),
      };
    }

    return {
      success: true,
      error: null,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }
}

/**
 * Refresh the current session
 *
 * Uses the refresh token to obtain a new access token.
 * Automatically called by Supabase client, but can be called manually if needed.
 *
 * @returns AuthResponse with new session or error
 *
 * @example
 * const { session, error } = await refreshSession();
 * if (!error) {
 *   console.log('Session refreshed');
 * }
 */
export async function refreshSession(): Promise<AuthResponse> {
  const client = getSupabaseClient();
  if (!client) {
    return {
      user: null,
      session: null,
      error: new Error('Supabase client not initialized'),
    };
  }

  try {
    const {
      data: { user, session },
      error,
    } = await client.auth.refreshSession();

    if (error) {
      return {
        user: null,
        session: null,
        error: new Error(error.message),
      };
    }

    return {
      user,
      session,
      error: null,
    };
  } catch (error) {
    return {
      user: null,
      session: null,
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }
}
